/**
 * The clients (tenants) a person can reach, as `GET /tenants` lists them
 * (document/61 M4), and what the interface may offer in each.
 *
 * Pure and framework-free: `tenants.test.ts` exercises it without React.
 */
import type { AccessibleTenant } from '@cms/contracts'
import type { ProjectAccessCapability } from './capabilities.js'
import { canManageTenantMembers } from './people.js'

interface Claims {
  roles: readonly string[]
  appRoles: readonly string[]
  scopes: readonly string[]
}

const ACCESS_ORDER: Record<AccessibleTenant['access'], number> = { home: 0, member: 1, operator: 2 }

/** Home first, then memberships, then the tenants only an operator sees; alphabetical inside each group. */
export function sortTenants(tenants: readonly AccessibleTenant[]): AccessibleTenant[] {
  return [...tenants].sort((left, right) =>
    ACCESS_ORDER[left.access] - ACCESS_ORDER[right.access] || left.name.localeCompare(right.name) || left.id.localeCompare(right.id))
}

/** The tenant the last request ran against: the gateway marks it `current`. */
export function currentTenant(tenants: readonly AccessibleTenant[]): AccessibleTenant | null {
  return tenants.find((tenant) => tenant.current) ?? tenants.find((tenant) => tenant.access === 'home') ?? tenants[0] ?? null
}

/**
 * What to remember when a person picks `tenantId`: null for the home tenant
 * (no header is its honest spelling) or when the tenant cannot be picked,
 * the id otherwise. `undefined` means "refuse the pick": an operator-only
 * tenant is listed, not selectable, and the header would answer 403.
 */
export function selectionFor(tenants: readonly AccessibleTenant[], tenantId: string): string | null | undefined {
  const tenant = tenants.find((entry) => entry.id === tenantId)
  if (!tenant || !tenant.selectable) return undefined
  return tenant.access === 'home' ? null : tenant.id
}

/** Whether the switcher is worth a place in the top bar: more than one tenant to see. */
export function showTenantSwitcher(tenants: readonly AccessibleTenant[]): boolean {
  return tenants.length > 1
}

/**
 * Whether to offer the people and key screens in this tenant. At home the
 * token's roles answer, as before. In a tenant picked with `X-Tenant` only
 * the CMS membership counts, and `/auth/sso/me` does not describe it, so the
 * entry is offered and the gateway's 403 is shown in plain words if the
 * membership carries no admin role. Hiding, never authorizing.
 */
export function canManageInTenant(person: Claims | null, tenant: AccessibleTenant | null): boolean {
  if (!person) return false
  if (!tenant || tenant.access === 'home') return canManageTenantMembers(person)
  return tenant.access === 'member'
}

/**
 * Whether to offer the `owner` role (invitation or role change): only an
 * owner may name one (the gateway's `requireTenantOwner`). Outside home the
 * membership is not described to the page, so the choice is offered and the
 * gateway answers.
 */
export function mayNameOwner(person: Claims | null, tenant: AccessibleTenant | null): boolean {
  if (!person) return false
  if (tenant && tenant.access !== 'home') return tenant.access === 'member'
  return [...person.roles, ...person.appRoles].some((role) => role === 'owner' || role === 'cms.owner')
}

/**
 * People management no longer waits for enforcement (document/60, 25
 * settembre): grants, roles and invitations are prepared while it is off.
 * Only a gateway that cannot say whether its writers are normalized keeps
 * the screen closed.
 */
export function peopleManagementOpen(capability: ProjectAccessCapability): boolean {
  return capability === 'enabled' || capability === 'disabled'
}
